import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { MagnifyingGlassIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  badgeClass,
  dangerButtonClass,
  DetailCard,
  DetailItem,
  Field,
  FormActions,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  isQuantity,
  primaryButtonClass,
  QUANTITY_STEP,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

type Status = 'draft' | 'submitted' | 'approved' | 'rejected' | 'closed' | 'cancelled';

interface IndentRow {
  id: string;
  indentNumber: string;
  indentDate: string;
  requiredBy: string | null;
  requestedFor: string | null;
  status: Status;
  requestedBy: string | null;
  items: number;
  products: string | null;
  overdue: boolean;
}
interface IndentPage {
  data: IndentRow[];
  total: number;
  totalPages: number;
}
interface IndentItem {
  id: string;
  productId: string;
  productName: string;
  sku: string;
  unit: string;
  quantity: number;
  orderedQuantity: number;
  pendingQuantity: number;
  onHand: number;
  notes: string | null;
}
interface Indent extends Omit<IndentRow, 'items' | 'products' | 'overdue'> {
  notes: string | null;
  closingReason: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  items: IndentItem[];
  rfqs: { id: string; rfqNumber: string; status: string }[];
}
interface Summary {
  drafts: number;
  awaitingApproval: number;
  toBuy: number;
  overdue: number;
}
interface ProductChoice {
  id: string;
  name: string;
  sku: string;
  unit: string;
  currentStock: number;
}
interface FormLine {
  productId: string;
  name: string;
  sku: string;
  unit: string;
  quantity: string;
  notes: string;
}

const PAGE_SIZES = [10, 25, 50, 100];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const STATUS: Record<Status, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'bg-gray-50 text-gray-700 ring-gray-500/20' },
  submitted: { label: 'Awaiting approval', className: 'bg-amber-50 text-amber-800 ring-amber-600/20' },
  approved: { label: 'Approved', className: 'bg-blue-50 text-blue-700 ring-blue-600/20' },
  rejected: { label: 'Rejected', className: 'bg-red-50 text-red-700 ring-red-600/20' },
  closed: { label: 'Ordered', className: 'bg-green-50 text-green-700 ring-green-600/20' },
  cancelled: { label: 'Cancelled', className: 'bg-gray-50 text-gray-500 ring-gray-500/20' },
};

const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
const formatDate = (value?: string | null) =>
  value ? new Date(String(value).length <= 10 ? `${value}T00:00:00` : value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const clean = (params: object) => Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== ''));

const indentApi = {
  page: async (params: object): Promise<IndentPage> => (await api.get('/indents', { params: clean(params) })).data,
  summary: async (): Promise<Summary> => (await api.get('/indents/summary')).data,
  one: async (id: string): Promise<Indent> => (await api.get(`/indents/${id}`)).data,
  create: async (data: object): Promise<Indent> => (await api.post('/indents', data)).data,
  update: async (id: string, data: object): Promise<Indent> => (await api.put(`/indents/${id}`, data)).data,
  act: async (id: string, action: string, data: object = {}): Promise<Indent> => (await api.post(`/indents/${id}/${action}`, data)).data,
  products: async (search: string): Promise<ProductChoice[]> => (await api.get('/inventory/paginated', { params: { search, limit: 8, page: 1 } })).data.data,
};

// ---------------------------------------------------------------- raise or change an indent

const IndentForm: React.FC<{ indent: Indent | null; isPending: boolean; onCancel: () => void; onSubmit: (data: object) => void }> = ({ indent, isPending, onCancel, onSubmit }) => {
  const [form, setForm] = useState({ requiredBy: indent?.requiredBy ?? '', requestedFor: indent?.requestedFor ?? '', notes: indent?.notes ?? '' });
  const [lines, setLines] = useState<FormLine[]>(() => (indent?.items ?? []).map((item) => ({ productId: item.productId, name: item.productName, sku: item.sku, unit: item.unit, quantity: String(item.quantity), notes: item.notes ?? '' })));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);
  const { data: found = [], isFetching } = useQuery({ queryKey: ['indents', 'products', search], queryFn: () => indentApi.products(search), enabled: search.length >= 2 });
  const choices = found.filter((product) => !lines.some((line) => line.productId === product.id));

  const setLine = (index: number, patch: Partial<FormLine>) => {
    setLines((prev) => prev.map((line, position) => (position === index ? { ...line, ...patch } : line)));
    setErrors((prev) => {
      const { [`line-${index}`]: _cleared, lines: _all, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (lines.length === 0) next.lines = 'Add at least one item';
    lines.forEach((line, index) => {
      const quantity = Number(line.quantity);
      if (line.quantity.trim() === '' || !isQuantity(quantity) || quantity <= 0) next[`line-${index}`] = 'Enter a quantity above zero, up to 4 decimals';
    });
    if (form.requiredBy && form.requiredBy < today()) next.requiredBy = 'Not in the past';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSubmit({
      requiredBy: form.requiredBy || null,
      requestedFor: form.requestedFor.trim() || null,
      notes: form.notes.trim() || null,
      items: lines.map((line) => ({ productId: line.productId, quantity: Number(line.quantity), notes: line.notes.trim() || null })),
    });
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Needed by" htmlFor="in-required" error={errors.requiredBy}>
          <input id="in-required" type="date" min={today()} value={form.requiredBy} onChange={(event) => setForm((prev) => ({ ...prev, requiredBy: event.target.value }))} className={cn(inputClass, errors.requiredBy && inputErrorClass)} />
        </Field>
        <Field label="Wanted for" htmlFor="in-for" hint="Department, machine or job">
          <input id="in-for" type="text" maxLength={150} value={form.requestedFor} onChange={(event) => setForm((prev) => ({ ...prev, requestedFor: event.target.value }))} className={inputClass} />
        </Field>
        <Field label="Notes" htmlFor="in-notes" className="sm:col-span-2">
          <input id="in-notes" type="text" maxLength={500} value={form.notes} onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))} className={inputClass} />
        </Field>
      </div>

      <section className="rounded-lg border border-gray-200">
        <h4 className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Items · {lines.length}</h4>
        {lines.length === 0 ? (
          <p className="px-3 py-4 text-sm text-gray-500">Nothing yet. Search below to add what is needed.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {lines.map((line, index) => (
              <li key={line.productId} className="space-y-1 px-3 py-2">
                <div className="flex flex-wrap items-center gap-3">
                  <p className="min-w-0 flex-1 text-sm text-gray-900">
                    {line.name} <span className="text-xs text-gray-500">{line.sku}</span>
                  </p>
                  <input type="number" min="0" step={QUANTITY_STEP} value={line.quantity} onChange={(event) => setLine(index, { quantity: event.target.value })} aria-label={`Quantity of ${line.name}`} className={cn(inputClass, 'w-28 py-1 text-right tabular-nums', errors[`line-${index}`] && inputErrorClass)} />
                  <span className="w-10 text-xs text-gray-500">{line.unit}</span>
                  <input type="text" maxLength={300} value={line.notes} onChange={(event) => setLine(index, { notes: event.target.value })} placeholder="Note" aria-label={`Note for ${line.name}`} className={cn(inputClass, 'w-48 py-1')} />
                  <button type="button" onClick={() => setLines((prev) => prev.filter((_, position) => position !== index))} aria-label={`Remove ${line.name}`} className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600">
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
                {errors[`line-${index}`] && <p className="text-sm text-red-600">{errors[`line-${index}`]}</p>}
              </li>
            ))}
          </ul>
        )}
        <div className="border-t border-gray-200 p-3">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input id="in-search" type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search product or SKU to add" aria-label="Search products to add" className={cn(inputClass, 'pl-9', errors.lines && inputErrorClass)} />
          </div>
          {errors.lines && <p className="mt-1 text-sm text-red-600">{errors.lines}</p>}
          {search.length >= 2 && (
            <ul className="mt-1 max-h-44 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
              {choices.length === 0 ? (
                <li className="px-3 py-2 text-sm text-gray-500">{isFetching ? 'Searching…' : 'No product matches'}</li>
              ) : (
                choices.map((product) => (
                  <li key={product.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setLines((prev) => [...prev, { productId: product.id, name: product.name, sku: product.sku, unit: product.unit, quantity: '', notes: '' }]);
                        setSearchInput('');
                        setSearch('');
                        setErrors((prev) => {
                          const { lines: _cleared, ...rest } = prev;
                          return rest;
                        });
                      }}
                      className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-indigo-50"
                    >
                      <span>
                        <span className="font-medium text-gray-900">{product.name}</span> <span className="text-gray-500">{product.sku}</span>
                      </span>
                      <span className="text-xs text-gray-500 tabular-nums">
                        {formatNumber(Number(product.currentStock))} {product.unit} in stock
                      </span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      </section>

      <FormActions>
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>
          Cancel
        </button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>
          {isPending ? 'Saving…' : indent ? 'Save Indent' : 'Raise Indent'}
        </button>
      </FormActions>
    </form>
  );
};

// ---------------------------------------------------------------- one indent

const IndentPanel: React.FC<{ indentId: string; canEdit: boolean; canApprove: boolean; onEdit: (indent: Indent) => void; onClose: () => void; onChanged: () => void }> = ({ indentId, canEdit, canApprove, onEdit, onClose, onChanged }) => {
  const queryClient = useQueryClient();
  const { data: indent, isLoading, isError, error } = useQuery({ queryKey: ['indents', 'one', indentId], queryFn: () => indentApi.one(indentId) });
  const [asking, setAsking] = useState<'reject' | 'cancel' | null>(null);
  const [reason, setReason] = useState('');

  const act = useMutation({
    mutationFn: ({ action, data }: { action: string; data?: object }) => indentApi.act(indentId, action, data),
    onSuccess: (saved, { action }) => {
      const verb: Record<string, string> = { submit: 'sent for approval', approve: 'approved', reject: 'rejected', cancel: 'cancelled' };
      toast.success(`${saved.indentNumber} ${verb[action]}`);
      queryClient.setQueryData(['indents', 'one', indentId], saved);
      setAsking(null);
      setReason('');
      onChanged();
    },
    onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'That could not be done')),
  });

  if (isLoading) return <p className="py-10 text-center text-sm text-gray-500">Loading…</p>;
  if (isError || !indent) return <p className="py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The indent could not be loaded.')}</p>;
  const open = ['draft', 'submitted', 'approved'].includes(indent.status);
  const pending = indent.items.some((item) => item.pendingQuantity > 0);

  return (
    <div className="space-y-4">
      <DetailCard title={indent.requestedFor || 'Purchase indent'}>
        <DetailItem label="Status"><span className={cn(badgeClass, STATUS[indent.status].className)}>{STATUS[indent.status].label}</span></DetailItem>
        <DetailItem label="Needed by">{formatDate(indent.requiredBy)}</DetailItem>
        <DetailItem label="Raised">{[formatDate(indent.indentDate), indent.requestedBy].filter(Boolean).join(' · ')}</DetailItem>
        <DetailItem label={indent.status === 'rejected' ? 'Rejected' : 'Approved'}>{indent.decidedAt ? [formatDate(indent.decidedAt), indent.decidedBy].filter(Boolean).join(' · ') : '—'}</DetailItem>
        {indent.notes && <DetailItem label="Notes" className="col-span-2 lg:col-span-4"><span className="font-normal">{indent.notes}</span></DetailItem>}
        {indent.closingReason && <DetailItem label="Reason" className="col-span-2 lg:col-span-4"><span className="font-normal">{indent.closingReason}</span></DetailItem>}
      </DetailCard>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className={thClass}>Item</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Asked for</th>
              <th scope="col" className={cn(thClass, 'text-right')}>On orders</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Still to order</th>
              <th scope="col" className={cn(thClass, 'text-right')}>In stock</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {indent.items.map((item) => (
              <tr key={item.id}>
                <td className="px-4 py-2 text-sm text-gray-900">
                  {item.productName} <span className="text-xs text-gray-500">{item.sku}</span>
                  {item.notes && <span className="block text-xs text-gray-500">{item.notes}</span>}
                </td>
                <td className="whitespace-nowrap px-4 py-2 text-right text-sm text-gray-900 tabular-nums">
                  {formatNumber(item.quantity)} <span className="text-gray-500">{item.unit}</span>
                </td>
                <td className="whitespace-nowrap px-4 py-2 text-right text-sm text-gray-700 tabular-nums">{item.orderedQuantity > 0 ? formatNumber(item.orderedQuantity) : '—'}</td>
                <td className={cn('whitespace-nowrap px-4 py-2 text-right text-sm tabular-nums', item.pendingQuantity > 0 ? 'font-medium text-gray-900' : 'text-gray-400')}>{item.pendingQuantity > 0 ? formatNumber(item.pendingQuantity) : '—'}</td>
                <td className="whitespace-nowrap px-4 py-2 text-right text-sm text-gray-700 tabular-nums">{formatNumber(item.onHand)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {indent.rfqs.length > 0 && (
        <p className="text-sm text-gray-700">
          RFQs raised:{' '}
          {indent.rfqs.map((rfq, index) => (
            <span key={rfq.id}>
              {index > 0 && ', '}
              <Link to={`/rfqs?open=${rfq.id}`} className="font-semibold text-indigo-600 hover:text-indigo-500">
                {rfq.rfqNumber}
              </Link>{' '}
              <span className="text-gray-500">({rfq.status})</span>
            </span>
          ))}
        </p>
      )}

      {asking ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <Field label={asking === 'reject' ? 'Why is it rejected?' : 'Why is it cancelled?'} htmlFor="ip-reason" required>
            <input id="ip-reason" type="text" maxLength={300} value={reason} onChange={(event) => setReason(event.target.value)} className={inputClass} />
          </Field>
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => { setAsking(null); setReason(''); }} className={secondaryButtonClass}>
              Back
            </button>
            <button type="button" disabled={act.isPending || reason.trim().length < 3} onClick={() => act.mutate({ action: asking, data: { reason: reason.trim() } })} className={dangerButtonClass}>
              {asking === 'reject' ? 'Confirm Rejection' : 'Confirm Cancelling'}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-3">
          <button type="button" onClick={onClose} className={secondaryButtonClass}>
            Close Window
          </button>
          {open && canEdit && (
            <button type="button" onClick={() => setAsking('cancel')} disabled={act.isPending} className={secondaryButtonClass}>
              Cancel Indent
            </button>
          )}
          {indent.status === 'draft' && canEdit && (
            <>
              <button type="button" onClick={() => onEdit(indent)} className={secondaryButtonClass}>
                Edit
              </button>
              <button type="button" onClick={() => act.mutate({ action: 'submit' })} disabled={act.isPending} className={primaryButtonClass}>
                Send for Approval
              </button>
            </>
          )}
          {indent.status === 'submitted' && canApprove && (
            <>
              <button type="button" onClick={() => setAsking('reject')} disabled={act.isPending} className={dangerButtonClass}>
                Reject
              </button>
              <button type="button" onClick={() => act.mutate({ action: 'approve' })} disabled={act.isPending} className={primaryButtonClass}>
                Approve
              </button>
            </>
          )}
          {indent.status === 'approved' && pending && canEdit && (
            <Link to={`/rfqs?indent=${indent.id}`} className={primaryButtonClass}>
              Raise RFQ
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- the page

const Indents: React.FC = () => {
  const { can } = usePermissions();
  const canCreate = can('purchase.create');
  const canEdit = can('purchase.update');
  const canApprove = can('purchase.approve');
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage());
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<Status | ''>('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Indent | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['indents', 'page', { page, limit, search, status: statusFilter }],
    queryFn: () => indentApi.page({ page, limit, search, status: statusFilter }),
    placeholderData: keepPreviousData,
  });
  const { data: summary } = useQuery({ queryKey: ['indents', 'summary'], queryFn: indentApi.summary });
  const { data: viewing } = useQuery({ queryKey: ['indents', 'one', viewingId], queryFn: () => indentApi.one(viewingId as string), enabled: !!viewingId });

  const refreshLists = () => ['page', 'summary'].forEach((key) => queryClient.invalidateQueries({ queryKey: ['indents', key] }));
  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };
  const save = useMutation({
    mutationFn: (input: object) => (editing ? indentApi.update(editing.id, input) : indentApi.create(input)),
    onSuccess: (indent) => {
      toast.success(editing ? `${indent.indentNumber} saved` : `${indent.indentNumber} raised`);
      queryClient.setQueryData(['indents', 'one', indent.id], indent);
      refreshLists();
      closeForm();
      setViewingId(indent.id);
    },
    onError: (submitError) => toast.error(getApiErrorMessage(submitError, 'The indent could not be saved')),
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const hasFilters = !!search || !!statusFilter;
  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);
  const stats = [
    { label: 'Drafts', value: formatNumber(summary?.drafts ?? 0), hint: 'Being written' },
    { label: 'Awaiting approval', value: formatNumber(summary?.awaitingApproval ?? 0), hint: 'Sent, not yet decided' },
    { label: 'To buy', value: formatNumber(summary?.toBuy ?? 0), hint: 'Approved, with something still to order' },
    { label: 'Past the date', value: formatNumber(summary?.overdue ?? 0), hint: 'Open indents needed before today' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Purchase Indents</h1>
          <p className="mt-1 text-sm text-gray-500">What the plant asks purchase to buy. An approved indent goes to vendors as an RFQ, and from there onto purchase orders.</p>
        </div>
        {canCreate && (
          <button type="button" onClick={() => setFormOpen(true)} className={primaryButtonClass}>
            <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Raise Indent
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search by indent number, what it is for, or product" aria-label="Search indents" className={cn(inputClass, 'pl-9')} />
          </div>
          <select value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value as Status | ''); setPage(1); }} aria-label="Status" className={cn(inputClass, 'lg:w-48')}>
            <option value="">All statuses</option>
            {(Object.keys(STATUS) as Status[]).map((status) => (<option key={status} value={status}>{STATUS[status].label}</option>))}
          </select>
        </div>

        <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className={thClass}>Indent</th>
                <th scope="col" className={thClass}>Items</th>
                <th scope="col" className={thClass}>Wanted for</th>
                <th scope="col" className={thClass}>Needed by</th>
                <th scope="col" className={thClass}>Status</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isError ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The indents could not be loaded.')}</td></tr>
              ) : isLoading ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <p className="text-sm font-medium text-gray-900">{hasFilters ? 'No indent matches' : 'No indents yet'}</p>
                    <p className="mt-1 text-sm text-gray-500">Raise an indent for material the plant needs bought.</p>
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3">
                      <button type="button" onClick={() => setViewingId(row.id)} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">{row.indentNumber}</button>
                      <p className="mt-0.5 text-xs text-gray-500">{[formatDate(row.indentDate), row.requestedBy].filter(Boolean).join(' · ')}</p>
                    </td>
                    <td className="max-w-sm px-4 py-3">
                      <p className="truncate text-sm text-gray-900" title={row.products ?? ''}>{row.products ?? '—'}</p>
                      <p className="mt-0.5 text-xs text-gray-500">{formatNumber(row.items)} item(s)</p>
                    </td>
                    <td className="max-w-xs truncate px-4 py-3 text-sm text-gray-700">{row.requestedFor || '—'}</td>
                    <td className={cn('whitespace-nowrap px-4 py-3 text-sm', row.overdue ? 'font-medium text-red-600' : 'text-gray-700')}>{formatDate(row.requiredBy)}</td>
                    <td className="whitespace-nowrap px-4 py-3"><span className={cn(badgeClass, STATUS[row.status].className)}>{STATUS[row.status].label}</span></td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button type="button" onClick={() => setViewingId(row.id)} className="rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50" aria-label={`Open ${row.indentNumber}`}>
                        Open
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            <span>Showing {firstRow}–{lastRow} of {formatNumber(total)}</span>
            <label className="flex items-center gap-2">
              Rows per page
              <select value={limit} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }} className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                {PAGE_SIZES.map((size) => (<option key={size} value={size}>{size}</option>))}
              </select>
            </label>
          </div>
          {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
        </div>
      </div>

      {/* One window: the form while raising or editing, the indent otherwise */}
      <Modal
        isOpen={formOpen || !!viewingId}
        onClose={() => (formOpen ? closeForm() : setViewingId(null))}
        title={formOpen ? (editing ? `Edit ${editing.indentNumber}` : 'Raise Purchase Indent') : viewing?.indentNumber ?? 'Purchase Indent'}
        size="xl"
      >
        {formOpen ? (
          <IndentForm key={editing?.id ?? 'new'} indent={editing} isPending={save.isPending} onCancel={closeForm} onSubmit={(input) => save.mutate(input)} />
        ) : (
          viewingId && (
            <IndentPanel
              key={viewingId}
              indentId={viewingId}
              canEdit={canEdit}
              canApprove={canApprove}
              onEdit={(indent) => {
                setEditing(indent);
                setFormOpen(true);
              }}
              onClose={() => setViewingId(null)}
              onChanged={refreshLists}
            />
          )
        )}
      </Modal>
    </div>
  );
};

export default Indents;
